import React, { useRef, useState } from "react";
import { useLanguage } from "../../context/LanguageContext";
import { translations } from "../../i18n/translations";
import { translations as skillTranslations, skillTranslationKeys, } from "../../i18n/ITCompetenciesSkills";
import { motion } from "framer-motion";
import type { FormStepProps } from "../../types/application";
import { applicationService } from "../../services/applicationService";
import { isMobile } from "react-device-detect";

const ReviewStep: React.FC<FormStepProps> = ({
  data,
  onNext,
  onPrevious,
  setSubmissionResult,
  navigateToStep,
}) => {
  const { language } = useLanguage();
  const t = translations[language] || translations.en;
  const skillT =
    skillTranslations[language as keyof typeof skillTranslations] ||
    skillTranslations.en;

  const translateEnhancedData = (field: string, value: string): string => {
    const translationMap: Record<string, Record<string, string>> = {
      citizenship: {
        "Canadian Citizen": t.citizenshipCanadian,
        "Permanent Resident": t.citizenshipPR,
        "Work Permit": t.citizenshipWorkPermit,
        "Other": t.citizenshipOther,
      },
      priorityEntitlementStatus: {
        "Yes": t.prioritiesYes,
        "No": t.prioritiesNo,
      },
      currentlyEmployedFPS: {
        "Yes": t.currentlyEmployedFPSYes,
        "No": t.currentlyEmployedFPSNo,
      },
      typeOfEmployment: {
        "Term": t.typeOfEmploymentTerm,
        "Indeterminate": t.typeOfEmploymentIndeterminate,
        "Student": t.typeOfEmploymentStudent,
        "Casual": t.typeOfEmploymentCasual,
        "Part-Time Worker": t.typeOfEmploymentPartTime,
      },
      priNumber: {
        "Yes": t.priNumberYes,
        "No": t.priNumberNo,
        "Maybe": t.priNumberMaybe,
      },
      qualifiedFullyAssessedPool: {
        "Yes": t.qualifiedFullyAssessedPoolYes,
        "No": t.qualifiedFullyAssessedPoolNo,
      },
      firstOfficialLanguage: {
        "English": t.firstOfficialLanguageEnglish,
        "French": t.firstOfficialLanguageFrench,
        "Other": t.firstOfficialLanguageOther,
      },
      validSLEResults: {
        "Reading": t.validSLEReading,
        "Writing": t.validSLEWriting,
        "Oral": t.validSLEOral,
        "No": t.validSLENo,
      },
      languageProfilesInterested: {
        "English Essential Positions": t.languageProfileEnglish,
        "French Essential Positions": t.languageProfileFrench,
        "Bilingual Positions": t.languageProfileBilingual,
      },
      validSecurityClearance: {
        "Reliability Status": t.validSecurityClearanceReliability,
        "Enhanced Reliability": t.validSecurityClearanceEnhanced,
        "Secret Clearance": t.validSecurityClearanceSecret,
        "Other": t.validSecurityClearanceOther,
        "No": t.validSecurityClearanceNo,
      },
      highestEducationLevel: {
        "Less than High School graduation": t.highestEducationLevelLessThanHS,
        "Secondary School Diploma": t.highestEducationLevelSecondary,
        "Post-Secondary Education": t.highestEducationLevelPostSecondary,
      },
      postSecondaryEducationLevel: {
        "Bachelor's Degree": t.postSecondaryEducationLevelBachelors,
        "College / Cégep Attestation": t.postSecondaryEducationLevelAttestation,
        "College / Cégep Certificate / Diploma": t.postSecondaryEducationLevelCertificate,
        "College / Cégep Applied Bachelor": t.postSecondaryEducationLevelAppliedBachelor,
        "University Credits / Certificate / Diploma": t.postSecondaryEducationLevelUniversityCredits,
        "Master's Degree": t.postSecondaryEducationLevelMasters,
        "Doctor of Medicine": t.postSecondaryEducationLevelMD,
        "Doctor of Philosophy (PhD)": t.postSecondaryEducationLevelPhD,
        "Graduate Diploma (DESS)": t.postSecondaryEducationLevelDESS,
      },
      lookingForITRole: {
        "Yes": t.lookingForITRoleYes,
        "Other": t.lookingForITRoleOther,
      },
    };

    const skillTranslationKey =
      skillTranslationKeys[value as keyof typeof skillTranslationKeys];

    return (
      translationMap[field]?.[value] ||
      (skillTranslationKey ? skillT[skillTranslationKey] : value)
    );
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionLockRef = useRef(false);
  const [submissionErrors, setSubmissionErrors] = useState<string[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const handleSubmit = async () => {
    if (submissionLockRef.current) return;

    submissionLockRef.current = true;
    setIsSubmitting(true);
    setSubmissionErrors([]);
    setValidationErrors([]);

    try {
      // Validate application data first
      const validation = applicationService.validateApplication(data);

      if (!validation.isValid) {
        setValidationErrors(validation.missingFields);
        setIsSubmitting(false);
        return;
      }

      // Submit application to backend
      const result = await applicationService.submitApplication(data, language);

      if (result.success) {
        // Store the application ID in the submission result
        if (setSubmissionResult && result.application_id) {
          setSubmissionResult({ applicationId: result.application_id });
        }
        // Application submitted successfully, proceed to success page
        onNext();
      } else {
        // Handle submission errors
        setSubmissionErrors(
          Array.isArray(result.errors) && result.errors.length > 0
            ? result.errors
            : [
                result.message ||
                  "An unexpected error occurred. Please try again.",
              ]
        );
      }
    } catch (error) {
      console.error("Submission error:", error);
      setSubmissionErrors(["An unexpected error occurred. Please try again."]);
    } finally {
      submissionLockRef.current = false;
      setIsSubmitting(false);
    }
  };

  const renderReviewQuestionLabel = (questionNumber: string, label: string) => (
    <span className="review-summary-label">
      <span className="review-summary-question-number">{questionNumber}</span>{" "}
      <span>{label}</span>
    </span>
  );

  const rawEnhancedData = data.enhancedData || {};
  const _translateArr = (field: string, arr: unknown) =>
    ((arr as string[]) || []).map((value) => translateEnhancedData(field, value));
  const enhancedData = {
    ...rawEnhancedData,
    programmingLanguages: _translateArr(
      "programmingLanguages",
      rawEnhancedData.programmingLanguages
    ),
    frameworks: _translateArr("frameworks", rawEnhancedData.frameworks),
    cloudOpsTools: _translateArr("cloudOpsTools", rawEnhancedData.cloudOpsTools),
    dataAnalyticsTools: _translateArr(
      "dataAnalyticsTools",
      rawEnhancedData.dataAnalyticsTools
    ),
    aiMlTools: _translateArr("aiMlTools", rawEnhancedData.aiMlTools),
    cybersecurityTools: _translateArr(
      "cybersecurityTools",
      rawEnhancedData.cybersecurityTools
    ),
    devOpsTools: _translateArr("devOpsTools", rawEnhancedData.devOpsTools),
    versionControlTools: _translateArr(
      "versionControlTools",
      rawEnhancedData.versionControlTools
    ),
    enterpriseTools: _translateArr("enterpriseTools", rawEnhancedData.enterpriseTools),
    projectCollabTools: _translateArr(
      "projectCollabTools",
      rawEnhancedData.projectCollabTools
    ),
    projectMgmtMethodologies: _translateArr(
      "projectMgmtMethodologies",
      rawEnhancedData.projectMgmtMethodologies
    ),
  };
  if (!isMobile)
    return (
      <motion.div
        className="space-y-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Application Summary */}
        <div className="review-summary bg-white border border-gray-200 rounded-lg divide-y divide-gray-200">
          {/* Attached Resume Section */}
          {(data.resumeFile || data.resumeUrl || data.documentId) && (
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t.attachedResume}
              </h3>
              <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center">
                  <svg
                    className="w-8 h-8 text-red-600 mr-3"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <p className="font-medium text-gray-900">
                      {data.resumeFile?.name ||
                        `Resume Document ${data.documentId ? `(ID: ${data.documentId.substring(0, 8)}...)` : ""}`}
                    </p>
                    <p className="text-sm text-gray-500">
                      {data.resumeFile
                        ? `${(data.resumeFile.size / 1024 / 1024).toFixed(2)} MB`
                        : "Uploaded successfully"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center text-green-600">
                  <svg
                    className="w-5 h-5 mr-1"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-sm font-medium">{t.attached}</span>
                </div>
              </div>
            </div>
          )}

          {/* Contact Information & Employment Status - Questions 1-5 */}
          <div className="p-6">
            <h3
              className="text-lg font-semibold text-gray-900 mb-4 cursor-pointer hover:text-primary-600 transition-colors flex items-center"
              onClick={() => navigateToStep?.(3)}
              title="Click to edit this section"
            >
              {t.sum1header}
              <svg
                className="w-4 h-4 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </h3>
            <ol className="review-summary-list" start={1}>
              <li className="review-summary-item">
                <span className="review-summary-label">{t.firstNameLabel}:</span>
                <span className="review-summary-value">{data.firstName || t.notSpecified}</span>
              </li>
              <li className="review-summary-item">
                <span className="review-summary-label">{t.lastNameLabel}:</span>
                <span className="review-summary-value">{data.lastName || t.notSpecified}</span>
              </li>
              <li className="review-summary-item">
                <span className="review-summary-label">{t.emailLabel}:</span>
                <span className="review-summary-value">{data.email || t.notSpecified}</span>
              </li>
              <li className="review-summary-item">
                <span className="review-summary-label">{t.citizenshipLabel}:</span>
                <span className="review-summary-value">
                  {translateEnhancedData("citizenship", enhancedData.citizenship || "") || t.notSpecified}
                </span>
              </li>
              <li className="review-summary-item">
                <span className="review-summary-label">{t.currentLocationEnd}:</span>
                <span className="review-summary-value">{enhancedData.currentLocation || t.notSpecified}</span>
              </li>
            </ol>
          </div>

          {/* Priority Entitlements - Question 6 */}
          <div className="p-6">
            <h3
              className="text-lg font-semibold text-gray-900 mb-4 cursor-pointer hover:text-primary-600 transition-colors flex items-center"
              onClick={() => navigateToStep?.(4)}
              title="Click to edit this section"
            >
              {t.sum2header}
              <svg
                className="w-4 h-4 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </h3>
            <ol className="review-summary-list" start={6}>
              <li className="review-summary-item">
                <span className="review-summary-label">{t.prorityEnd}:</span>
                <span className="review-summary-value">
                  {translateEnhancedData("priorityEntitlementStatus", enhancedData.priorityEntitlementStatus || "") || t.notSpecified}
                </span>
              </li>
            </ol>
            {enhancedData.priorityEntitlements &&
              (enhancedData.priorityEntitlements as string[]).length > 0 && (
                <div className="mt-4">
                  <span className="font-medium text-gray-700">
                    Selected Priority Entitlements:
                  </span>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(enhancedData.priorityEntitlements as string[]).map(
                      (entitlement, index) => (
                        <span
                          key={index}
                          className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                        >
                          {entitlement}
                        </span>
                      )
                    )}
                  </div>
                </div>
              )}
          </div>

          {/* Employment Status - Questions 7-14 */}
          <div className="p-6">
            <h3
              className="text-lg font-semibold text-gray-900 mb-4 cursor-pointer hover:text-primary-600 transition-colors flex items-center"
              onClick={() => navigateToStep?.(5)}
              title="Click to edit this section"
            >
              {t.sum3header}
              <svg
                className="w-4 h-4 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </h3>
            <ol className="review-summary-list" start={7}>
              <li className="review-summary-item">
                {renderReviewQuestionLabel("7.", `${t.currentlyEmployedFPSLabel}:`)}
                <span className="review-summary-value">
                  {translateEnhancedData("currentlyEmployedFPS", enhancedData.currentlyEmployedFPS || "") || t.notSpecified}
                </span>
              </li>
              <li className="review-summary-item">
                {renderReviewQuestionLabel("8.", `${t.currentClassificationLabel || "Current Classification"}:`)}
                <span className="review-summary-value">{enhancedData.currentClassification || t.notSpecified}</span>
              </li>
              <li className="review-summary-item">
                {renderReviewQuestionLabel("8.5.", `${t.currentLevel || "Current Level"}:`)}
                <span className="review-summary-value">{enhancedData.currentLevel || t.notSpecified}</span>
              </li>
              <li className="review-summary-item">
                {renderReviewQuestionLabel("9.", `${t.typeofEmployment}:`)}
                <span className="review-summary-value">
                  {translateEnhancedData("typeOfEmployment", enhancedData.typeOfEmployment || "") || t.notSpecified}
                </span>
              </li>
              <li className="review-summary-item">
                {renderReviewQuestionLabel("10.", `${t.employmentEndDate}:`)}
                <span className="review-summary-value">{enhancedData.employmentEndDate || t.notSpecified}</span>
              </li>
              <li className="review-summary-item">
                {renderReviewQuestionLabel("11.", `${t.priNumber}:`)}
                <span className="review-summary-value">
                  {translateEnhancedData("priNumber", enhancedData.priNumber || "") || t.notSpecified}
                </span>
              </li>
              <li className="review-summary-item">
                {renderReviewQuestionLabel("12.", `${t.qualifiedFullyAssessedPoolLabel}:`)}
                <span className="review-summary-value">
                  {translateEnhancedData("qualifiedFullyAssessedPool", enhancedData.qualifiedFullyAssessedPool || "") || t.notSpecified}
                </span>
              </li>
              <li className="review-summary-item">
                {renderReviewQuestionLabel("13.", `${t.staffingProcess}:`)}
                <span className="review-summary-value">{enhancedData.staffingProcess || t.notSpecified}</span>
              </li>
              <li className="review-summary-item">
                {renderReviewQuestionLabel("14.", `${t.GroupFullyAssessed}:`)}
                <span className="review-summary-value">{enhancedData.GroupFullyAssessed || t.notSpecified}</span>
              </li>
              <li className="review-summary-item">
                {renderReviewQuestionLabel("14.5.", `${t.LevelFullyAssessed}:`)}
                <span className="review-summary-value">{enhancedData.LevelFullyAssessed || t.notSpecified}</span>
              </li>
            </ol>
          </div>

          {/* Language & SLE Information - Questions 15-19 */}
          <div className="p-6">
            <h3
              className="text-lg font-semibold text-gray-900 mb-4 cursor-pointer hover:text-primary-600 transition-colors flex items-center"
              onClick={() => navigateToStep?.(6)}
              title="Click to edit this section"
            >
              {t.languageSLEHeader}
              <svg
                className="w-4 h-4 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </h3>
            <ol className="review-summary-list" start={15}>
              <li className="review-summary-item">
                {renderReviewQuestionLabel("15.", `${t.firstOfficialLanguageLabel || "First Official Language"}:`)}
                <span className="review-summary-value">
                  {translateEnhancedData("firstOfficialLanguage", enhancedData.firstOfficialLanguage || "") || t.notSpecified}
                </span>
              </li>
              <li className="review-summary-item review-summary-item--stacked">
                {renderReviewQuestionLabel("16.", `${t.validSLEResults}:`)}
                <span className="review-summary-value review-summary-tag-list">
                  {enhancedData.validSLEResults && (enhancedData.validSLEResults as string[]).length > 0 ? (
                    (enhancedData.validSLEResults as string[]).map((result, index) => (
                      <span key={index} className="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                        {translateEnhancedData("validSLEResults", result)}
                      </span>
                    ))
                  ) : (
                    t.notSpecified
                  )}
                </span>
              </li>
              <li className="review-summary-item">
                {renderReviewQuestionLabel("17.", `${t.sleLevelLabel || "SLE Level"}:`)}
                <span className="review-summary-value">{enhancedData.sleLevel || t.notSpecified}</span>
              </li>
              <li className="review-summary-item">
                {renderReviewQuestionLabel("18.", `${t.sleExpiryDateLabel || "SLE Expiry Date"}:`)}
                <span className="review-summary-value">{enhancedData.sleExpiryDate || t.notSpecified}</span>
              </li>
              <li className="review-summary-item review-summary-item--stacked">
                {renderReviewQuestionLabel("19.", `${t.languageProfilesLabel}:`)}
                <span className="review-summary-value review-summary-tag-list">
                  {enhancedData.languageProfilesInterested && (enhancedData.languageProfilesInterested as string[]).length > 0 ? (
                    (enhancedData.languageProfilesInterested as string[]).map((result, index) => (
                      <span key={index} className="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                        {translateEnhancedData("languageProfilesInterested", result)}
                      </span>
                    ))
                  ) : (
                    t.notSpecified
                  )}
                </span>
              </li>
            </ol>
          </div>

          {/* Language & SLE Information - Questions 20-26 */}
          <div className="p-6">
            <h3
              className="text-lg font-semibold text-gray-900 mb-4 cursor-pointer hover:text-primary-600 transition-colors flex items-center"
              onClick={() => navigateToStep?.(7)}
              title="Click to edit this section"
            >
              {t.statusSecurityEducation}
              <svg
                className="w-4 h-4 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </h3>
            <ol className="review-summary-list" start={20}>
              <li className="review-summary-item">
                {renderReviewQuestionLabel("20.", `${t.validSecurityClearanceLabel || "Security Clearance"}:`)}
                <span className="review-summary-value">
                  {translateEnhancedData("validSecurityClearance", enhancedData.validSecurityClearance || "") || t.notSpecified}
                </span>
              </li>
              <li className="review-summary-item">
                {renderReviewQuestionLabel("21.", `${t.highestEducationLevelLabel || "Highest Education"}:`)}
                <span className="review-summary-value">
                  {translateEnhancedData("highestEducationLevel", enhancedData.highestEducationLevel || "") || t.notSpecified}
                </span>
              </li>
              <li className="review-summary-item">
                {renderReviewQuestionLabel("22.", `${t.postSecondaryEducationLevelLabel || "Post-Secondary Level"}:`)}
                <span className="review-summary-value">
                  {translateEnhancedData("postSecondaryEducationLevel", enhancedData.postSecondaryEducationLevel || "") || t.notSpecified}
                </span>
              </li>
              <li className="review-summary-item">
                {renderReviewQuestionLabel("23.", `${t.postSecondaryInstitutionLabel || "Institution"}:`)}
                <span className="review-summary-value">{enhancedData.postSecondaryInstitution || t.notSpecified}</span>
              </li>
              <li className="review-summary-item">
                {renderReviewQuestionLabel("24.", `${t.areaOfStudyLabel || "Area of Study"}:`)}
                <span className="review-summary-value">{enhancedData.areaOfStudy || t.notSpecified}</span>
              </li>
              <li className="review-summary-item">
                {renderReviewQuestionLabel("25.", `${t.graduationDateLabel || "Graduation Date"}:`)}
                <span className="review-summary-value">{enhancedData.graduationDate || t.notSpecified}</span>
              </li>
              <li className="review-summary-item">
                {renderReviewQuestionLabel("26.", `${t.lookingForITRoleLabel || "Looking for IT Role"}:`)}
                <span className="review-summary-value">
                  {translateEnhancedData("lookingForITRole", enhancedData.lookingForITRole || "") || t.notSpecified}
                </span>
              </li>
            </ol>
          </div>

          {/* IT Competencies Summary - Questions 27-37 */}
          <div className="p-6">
            <h3
              className="text-lg font-semibold text-gray-900 mb-4 cursor-pointer hover:text-primary-600 transition-colors flex items-center"
              onClick={() => navigateToStep?.(8)}
              title="Click to edit this section"
            >
              {t.sum5header}
              <svg
                className="w-4 h-4 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </h3>

            {/* Technical Skills Summary */}
            <ol className="review-summary-list review-summary-list--stacked" start={27}>
              {enhancedData.programmingLanguages && (enhancedData.programmingLanguages as string[]).length > 0 && (
                <li className="review-summary-item review-summary-item--stacked">
                  {renderReviewQuestionLabel("27.", `${t.q27 || "Programming Languages"}`)}
                  <span className="review-summary-value review-summary-tag-list">
                    {(enhancedData.programmingLanguages as string[]).map((lang, index) => (
                      <span key={index} className="inline-block px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">{lang}</span>
                    ))}
                    {enhancedData.programmingLanguages_other && (
                      <span className="text-sm text-gray-600">
                        <strong>{t.other}:</strong> {enhancedData.programmingLanguages_other}
                      </span>
                    )}
                  </span>
                </li>
              )}

              {enhancedData.frameworks && (enhancedData.frameworks as string[]).length > 0 && (
                <li className="review-summary-item review-summary-item--stacked">
                  {renderReviewQuestionLabel("28.", `${t.q28 || "Frameworks & Libraries"}`)}
                  <span className="review-summary-value review-summary-tag-list">
                    {(enhancedData.frameworks as string[]).map((framework, index) => (
                      <span key={index} className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">{framework}</span>
                    ))}
                    {enhancedData.frameworks_other && (
                      <span className="text-sm text-gray-600">
                        <strong>{t.other}:</strong> {enhancedData.frameworks_other}
                      </span>
                    )}
                  </span>
                </li>
              )}

              {enhancedData.cloudOpsTools && (enhancedData.cloudOpsTools as string[]).length > 0 && (
                <li className="review-summary-item review-summary-item--stacked">
                  {renderReviewQuestionLabel("29.", `${t.q29 || "Cloud Operations & Tools"}`)}
                  <span className="review-summary-value review-summary-tag-list">
                    {(enhancedData.cloudOpsTools as string[]).map((tool, index) => (
                      <span key={index} className="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs rounded">{tool}</span>
                    ))}
                    {enhancedData.cloudOpsTools_other && (
                      <span className="text-sm text-gray-600">
                        <strong>{t.other}:</strong> {enhancedData.cloudOpsTools_other}
                      </span>
                    )}
                  </span>
                </li>
              )}

              {enhancedData.dataAnalyticsTools && (enhancedData.dataAnalyticsTools as string[]).length > 0 && (
                <li className="review-summary-item review-summary-item--stacked">
                  {renderReviewQuestionLabel("30.", `${t.q30}`)}
                  <span className="review-summary-value review-summary-tag-list">
                    {(enhancedData.dataAnalyticsTools as string[]).map((tool, index) => (
                      <span key={index} className="inline-block px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">{tool}</span>
                    ))}
                    {enhancedData.dataAnalyticsTools_other && (
                      <span className="text-sm text-gray-600">
                        <strong>{t.other}:</strong> {enhancedData.dataAnalyticsTools_other}
                      </span>
                    )}
                  </span>
                </li>
              )}

              {enhancedData.aiMlTools && (enhancedData.aiMlTools as string[]).length > 0 && (
                <li className="review-summary-item review-summary-item--stacked">
                  {renderReviewQuestionLabel("31.", `${t.q31}`)}
                  <span className="review-summary-value review-summary-tag-list">
                    {(enhancedData.aiMlTools as string[]).map((tool, index) => (
                      <span key={index} className="inline-block px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded">{tool}</span>
                    ))}
                    {enhancedData.aiMlTools_other && (
                      <span className="text-sm text-gray-600">
                        <strong>{t.other}:</strong> {enhancedData.aiMlTools_other}
                      </span>
                    )}
                  </span>
                </li>
              )}

              {enhancedData.cybersecurityTools && (enhancedData.cybersecurityTools as string[]).length > 0 && (
                <li className="review-summary-item review-summary-item--stacked">
                  {renderReviewQuestionLabel("32.", `${t.q32}`)}
                  <span className="review-summary-value review-summary-tag-list">
                    {(enhancedData.cybersecurityTools as string[]).map((tool, index) => (
                      <span key={index} className="inline-block px-2 py-1 bg-red-100 text-red-800 text-xs rounded">{tool}</span>
                    ))}
                    {enhancedData.cybersecurityTools_other && (
                      <span className="text-sm text-gray-600">
                        <strong>{t.other}:</strong> {enhancedData.cybersecurityTools_other}
                      </span>
                    )}
                  </span>
                </li>
              )}

              {enhancedData.devOpsTools && (enhancedData.devOpsTools as string[]).length > 0 && (
                <li className="review-summary-item review-summary-item--stacked">
                  {renderReviewQuestionLabel("33.", `${t.q33}`)}
                  <span className="review-summary-value review-summary-tag-list">
                    {(enhancedData.devOpsTools as string[]).map((tool, index) => (
                      <span key={index} className="inline-block px-2 py-1 bg-teal-100 text-teal-800 text-xs rounded">{tool}</span>
                    ))}
                    {enhancedData.devOpsTools_other && (
                      <span className="text-sm text-gray-600">
                        <strong>{t.other}:</strong> {enhancedData.devOpsTools_other}
                      </span>
                    )}
                  </span>
                </li>
              )}

              {enhancedData.versionControlTools && (enhancedData.versionControlTools as string[]).length > 0 && (
                <li className="review-summary-item review-summary-item--stacked">
                  {renderReviewQuestionLabel("34.", `${t.q34}`)}
                  <span className="review-summary-value review-summary-tag-list">
                    {(enhancedData.versionControlTools as string[]).map((tool, index) => (
                      <span key={index} className="inline-block px-2 py-1 bg-pink-100 text-pink-800 text-xs rounded">{tool}</span>
                    ))}
                    {enhancedData.versionControlTools_other && (
                      <span className="text-sm text-gray-600">
                        <strong>{t.other}:</strong> {enhancedData.versionControlTools_other}
                      </span>
                    )}
                  </span>
                </li>
              )}

              {enhancedData.enterpriseTools && (enhancedData.enterpriseTools as string[]).length > 0 && (
                <li className="review-summary-item review-summary-item--stacked">
                  {renderReviewQuestionLabel("35.", `${t.q35}`)}
                  <span className="review-summary-value review-summary-tag-list">
                    {(enhancedData.enterpriseTools as string[]).map((tool, index) => (
                      <span key={index} className="inline-block px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">{tool}</span>
                    ))}
                    {enhancedData.enterpriseTools_other && (
                      <span className="text-sm text-gray-600">
                        <strong>{t.other}:</strong> {enhancedData.enterpriseTools_other}
                      </span>
                    )}
                  </span>
                </li>
              )}

              {enhancedData.projectCollabTools && (enhancedData.projectCollabTools as string[]).length > 0 && (
                <li className="review-summary-item review-summary-item--stacked">
                  {renderReviewQuestionLabel("36.", `${t.q36}`)}
                  <span className="review-summary-value review-summary-tag-list">
                    {(enhancedData.projectCollabTools as string[]).map((tool, index) => (
                      <span key={index} className="inline-block px-2 py-1 bg-cyan-100 text-cyan-800 text-xs rounded">{tool}</span>
                    ))}
                    {enhancedData.projectCollabTools_other && (
                      <span className="text-sm text-gray-600">
                        <strong>{t.other}:</strong> {enhancedData.projectCollabTools_other}
                      </span>
                    )}
                  </span>
                </li>
              )}

              {enhancedData.projectMgmtMethodologies && (enhancedData.projectMgmtMethodologies as string[]).length > 0 && (
                <li className="review-summary-item review-summary-item--stacked">
                  {renderReviewQuestionLabel("37.", `${t.q37}`)}
                  <span className="review-summary-value review-summary-tag-list">
                    {(enhancedData.projectMgmtMethodologies as string[]).map((methodology, index) => (
                      <span key={index} className="inline-block px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">{methodology}</span>
                    ))}
                    {enhancedData.projectMgmtMethodologies_other && (
                      <span className="text-sm text-gray-600">
                        <strong>{t.other}:</strong> {enhancedData.projectMgmtMethodologies_other}
                      </span>
                    )}
                  </span>
                </li>
              )}
            </ol>
          </div>

          {/* Additional Information - Questions 38 */}
          {(enhancedData.additionalSkills ||
            enhancedData.greatestAchievement ||
            enhancedData.additionalInformation) && (
            <div className="p-6">
              <h3
                className="text-lg font-semibold text-gray-900 mb-4 cursor-pointer hover:text-primary-600 transition-colors flex items-center"
                onClick={() => navigateToStep?.(8)}
                title="Click to edit this section"
              >
                {t.additionalInfoHeader}
                <svg
                  className="w-4 h-4 ml-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </h3>
              <div className="space-y-4 text-sm">
                {enhancedData.additionalInformation && (
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">
                      38. {t.q40}
                    </h4>
                    <p className="text-gray-600 bg-gray-50 p-3 rounded-md">
                      {enhancedData.additionalInformation}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Validation and Submission Errors */}
        {(validationErrors.length > 0 || submissionErrors.length > 0) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  {validationErrors.length > 0
                    ? "Missing Required Information"
                    : t.submissionError}
                </h3>
                <div
                  className="mt-2 text-sm text-red-700"
                  style={{ whiteSpace: "pre-line" }}
                >
                  <ul className="list-disc list-inside space-y-1">
                    {validationErrors.map((error, index) => (
                      <li key={index}>
                        {"Please provide:"} {error}
                      </li>
                    ))}
                    {submissionErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Navigation */}
        {isMobile ? (
          <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onPrevious}
              className="w-full sm:w-auto px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              {t.previousButton || "Previous"}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`w-full sm:w-auto px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center ${
                isSubmitting
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed border-2 border-gray-500"
                  : "bg-[#D7E5F5] text-[#2B4380] hover:bg-[#D0DFF0] border-2 border-[#2B4380]"
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {"Submitting Application..."}
                </>
              ) : (
                t.submitApplicationButton || "Submit Application"
              )}
            </button>
          </div>
        ) : (
          <div className="flex justify-between pt-6">
            <button
              type="button"
              onClick={onPrevious}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              {t.previousButton || "Previous"}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center ${
                isSubmitting
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed border-2 border-gray-500"
                  : "bg-[#D7E5F5] text-[#2B4380] hover:bg-[#D0DFF0] border-2 border-[#2B4380]"
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {"Submitting Application..."}
                </>
              ) : (
                t.submitApplicationButton || "Submit Application"
              )}
            </button>
          </div>
        )}
      </motion.div>
    );
  else
    return (
      <motion.div
        className="space-y-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Application Summary */}
        <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-200">
          <div className="p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t.applicationSummaryHeader}
            </h2>
            <p className="text-gray-600">{t.applicationSummarySubheader}</p>
          </div>

          {/* Attached Resume Section */}
          {(data.resumeFile || data.resumeUrl || data.documentId) && (
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t.attachedResume}
              </h3>
              <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center">
                  <svg
                    className="w-8 h-8 text-red-600 mr-3"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <p className="font-medium text-gray-900">
                      {data.resumeFile?.name ||
                        `Resume Document ${data.documentId ? `(ID: ${data.documentId.substring(0, 8)}...)` : ""}`}
                    </p>
                    <p className="text-sm text-gray-500">
                      {data.resumeFile
                        ? `${(data.resumeFile.size / 1024 / 1024).toFixed(2)} MB`
                        : "Uploaded successfully"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center text-green-600">
                  <svg
                    className="w-5 h-5 mr-1"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-sm font-medium">{t.attached}</span>
                </div>
              </div>
            </div>
          )}

          {/* Contact Information & Employment Status - Questions 1-5 */}
          <div className="p-6">
            <h3
              className="text-lg font-semibold text-gray-900 mb-4 cursor-pointer hover:text-primary-600 transition-colors flex items-center"
              onClick={() => navigateToStep?.(3)}
              title="Click to edit this section"
            >
              {t.sum1header}
              <svg
                className="w-4 h-4 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </h3>
            <ol className="review-summary-list" start={1}>
              <li className="review-summary-item">
                <span className="review-summary-label">{t.firstNameLabel}:</span>
                <span className="review-summary-value">{data.firstName || t.notSpecified}</span>
              </li>
              <li className="review-summary-item">
                <span className="review-summary-label">{t.lastNameLabel}:</span>
                <span className="review-summary-value">{data.lastName || t.notSpecified}</span>
              </li>
              <li className="review-summary-item">
                <span className="review-summary-label">{t.emailLabel}:</span>
                <span className="review-summary-value">{data.email || t.notSpecified}</span>
              </li>
              <li className="review-summary-item">
                <span className="review-summary-label">{t.citizenshipLabel}:</span>
                <span className="review-summary-value">
                  {translateEnhancedData("citizenship", enhancedData.citizenship || "") || t.notSpecified}
                </span>
              </li>
              <li className="review-summary-item">
                <span className="review-summary-label">{t.currentLocationEnd}:</span>
                <span className="review-summary-value">{enhancedData.currentLocation || t.notSpecified}</span>
              </li>
            </ol>
          </div>

          {/* Priority Entitlements - Question 6 */}
          <div className="p-6">
            <h3
              className="text-lg font-semibold text-gray-900 mb-4 cursor-pointer hover:text-primary-600 transition-colors flex items-center"
              onClick={() => navigateToStep?.(4)}
              title="Click to edit this section"
            >
              {t.sum2header}
              <svg
                className="w-4 h-4 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </h3>
            <ol className="review-summary-list" start={6}>
              <li className="review-summary-item">
                <span className="review-summary-label">{t.prorityEnd}:</span>
                <span className="review-summary-value">
                  {translateEnhancedData("priorityEntitlementStatus", enhancedData.priorityEntitlementStatus || "") || t.notSpecified}
                </span>
              </li>
            </ol>
            {enhancedData.priorityEntitlements &&
              (enhancedData.priorityEntitlements as string[]).length > 0 && (
                <div className="mt-4">
                  <span className="font-medium text-gray-700">
                    Selected Priority Entitlements:
                  </span>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(enhancedData.priorityEntitlements as string[]).map(
                      (entitlement, index) => (
                        <span
                          key={index}
                          className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                        >
                          {entitlement}
                        </span>
                      )
                    )}
                  </div>
                </div>
              )}
          </div>

          {/* Employment Status - Questions 7-14 */}
          <div className="p-6">
            <h3
              className="text-lg font-semibold text-gray-900 mb-4 cursor-pointer hover:text-primary-600 transition-colors flex items-center"
              onClick={() => navigateToStep?.(5)}
              title="Click to edit this section"
            >
              {t.sum3header}
              <svg
                className="w-4 h-4 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </h3>
            <ol className="review-summary-list" start={7}>
              <li className="review-summary-item">
                <span className="review-summary-label">{t.currentlyEmployedFPSLabel}:</span>
                <span className="review-summary-value">
                  {translateEnhancedData("currentlyEmployedFPS", enhancedData.currentlyEmployedFPS || "") || t.notSpecified}
                </span>
              </li>
              <li className="review-summary-item">
                <span className="review-summary-label">{t.currentClassificationLabel || "Current Classification:"}:</span>
                <span className="review-summary-value">{enhancedData.currentClassification || t.notSpecified}</span>
              </li>
              <li className="review-summary-item">
                <span className="review-summary-label">{t.currentLevel || "Current Level:"}:</span>
                <span className="review-summary-value">{enhancedData.currentLevel || t.notSpecified}</span>
              </li>
              <li className="review-summary-item">
                <span className="review-summary-label">{t.typeofEmployment}:</span>
                <span className="review-summary-value">
                  {translateEnhancedData("typeOfEmployment", enhancedData.typeOfEmployment || "") || t.notSpecified}
                </span>
              </li>
              <li className="review-summary-item">
                <span className="review-summary-label">{t.employmentEndDate}:</span>
                <span className="review-summary-value">{enhancedData.employmentEndDate || t.notSpecified}</span>
              </li>
              <li className="review-summary-item">
                <span className="review-summary-label">{t.priNumber}:</span>
                <span className="review-summary-value">
                  {translateEnhancedData("priNumber", enhancedData.priNumber || "") || t.notSpecified}
                </span>
              </li>
              <li className="review-summary-item">
                <span className="review-summary-label">{t.qualifiedFullyAssessedPoolLabel}:</span>
                <span className="review-summary-value">
                  {translateEnhancedData("qualifiedFullyAssessedPool", enhancedData.qualifiedFullyAssessedPool || "") || t.notSpecified}
                </span>
              </li>
              <li className="review-summary-item">
                <span className="review-summary-label">{t.staffingProcess}:</span>
                <span className="review-summary-value">{enhancedData.staffingProcess || t.notSpecified}</span>
              </li>
              <li className="review-summary-item">
                <span className="review-summary-label">{t.GroupFullyAssessed}:</span>
                <span className="review-summary-value">{enhancedData.GroupFullyAssessed || t.notSpecified}</span>
              </li>
              <li className="review-summary-item">
                <span className="review-summary-label">{t.LevelFullyAssessed}:</span>
                <span className="review-summary-value">{enhancedData.LevelFullyAssessed || t.notSpecified}</span>
              </li>
            </ol>
          </div>

          {/* Language & SLE Information - Questions 20-26 */}
          <div className="p-6">
            <h3
              className="text-lg font-semibold text-gray-900 mb-4 cursor-pointer hover:text-primary-600 transition-colors flex items-center"
              onClick={() => navigateToStep?.(7)}
              title="Click to edit this section"
            >
              {t.statusSecurityEducation}
              <svg
                className="w-4 h-4 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </h3>
            <ol className="review-summary-list" start={20}>
              <li className="review-summary-item">
                <span className="review-summary-label">{t.validSecurityClearanceLabel || "Security Clearance:"}</span>
                <span className="review-summary-value">
                  {translateEnhancedData("validSecurityClearance", enhancedData.validSecurityClearance || "") || t.notSpecified}
                </span>
              </li>
              <li className="review-summary-item">
                <span className="review-summary-label">{t.highestEducationLevelLabel || "Highest Education:"}</span>
                <span className="review-summary-value">
                  {translateEnhancedData("highestEducationLevel", enhancedData.highestEducationLevel || "") || t.notSpecified}
                </span>
              </li>
              <li className="review-summary-item">
                <span className="review-summary-label">{t.postSecondaryEducationLevelLabel || "Post-Secondary Level:"}</span>
                <span className="review-summary-value">
                  {translateEnhancedData("postSecondaryEducationLevel", enhancedData.postSecondaryEducationLevel || "") || t.notSpecified}
                </span>
              </li>
              <li className="review-summary-item">
                <span className="review-summary-label">{t.postSecondaryInstitutionLabel || "Institution:"}</span>
                <span className="review-summary-value">{enhancedData.postSecondaryInstitution || t.notSpecified}</span>
              </li>
              <li className="review-summary-item">
                <span className="review-summary-label">{t.areaOfStudyLabel || "Area of Study:"}</span>
                <span className="review-summary-value">{enhancedData.areaOfStudy || t.notSpecified}</span>
              </li>
              <li className="review-summary-item">
                <span className="review-summary-label">{t.graduationDateLabel || "Graduation Date:"}</span>
                <span className="review-summary-value">{enhancedData.graduationDate || t.notSpecified}</span>
              </li>
              <li className="review-summary-item">
                <span className="review-summary-label">{t.lookingForITRoleLabel || "Looking for IT Role:"}</span>
                <span className="review-summary-value">
                  {translateEnhancedData("lookingForITRole", enhancedData.lookingForITRole || "") || t.notSpecified}
                </span>
              </li>
            </ol>
          </div>

          {/* IT Competencies Summary - Questions 27-37 */}
          <div className="p-6">
            <h3
              className="text-lg font-semibold text-gray-900 mb-4 cursor-pointer hover:text-primary-600 transition-colors flex items-center"
              onClick={() => navigateToStep?.(8)}
              title="Click to edit this section"
            >
              {t.sum5header}
              <svg
                className="w-4 h-4 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </h3>

            {/* Technical Skills Summary */}
            <ol className="review-summary-list review-summary-list--stacked" start={27}>
              {enhancedData.programmingLanguages && (enhancedData.programmingLanguages as string[]).length > 0 && (
                <li className="review-summary-item review-summary-item--stacked">
                  <span className="review-summary-label">{t.q27 || "Programming Languages"}</span>
                  <span className="review-summary-value review-summary-tag-list">
                    {(enhancedData.programmingLanguages as string[]).map((lang, index) => (
                      <span key={index} className="inline-block px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">{lang}</span>
                    ))}
                    {enhancedData.programmingLanguages_other && (
                      <span className="text-sm text-gray-600">
                        <strong>{t.other}:</strong> {enhancedData.programmingLanguages_other}
                      </span>
                    )}
                  </span>
                </li>
              )}

              {enhancedData.frameworks && (enhancedData.frameworks as string[]).length > 0 && (
                <li className="review-summary-item review-summary-item--stacked">
                  <span className="review-summary-label">{t.q28 || "Frameworks & Libraries"}</span>
                  <span className="review-summary-value review-summary-tag-list">
                    {(enhancedData.frameworks as string[]).map((framework, index) => (
                      <span key={index} className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">{framework}</span>
                    ))}
                    {enhancedData.frameworks_other && (
                      <span className="text-sm text-gray-600">
                        <strong>{t.other}:</strong> {enhancedData.frameworks_other}
                      </span>
                    )}
                  </span>
                </li>
              )}

              {enhancedData.cloudOpsTools && (enhancedData.cloudOpsTools as string[]).length > 0 && (
                <li className="review-summary-item review-summary-item--stacked">
                  <span className="review-summary-label">{t.q29 || "Cloud Operations & Tools"}</span>
                  <span className="review-summary-value review-summary-tag-list">
                    {(enhancedData.cloudOpsTools as string[]).map((tool, index) => (
                      <span key={index} className="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs rounded">{tool}</span>
                    ))}
                    {enhancedData.cloudOpsTools_other && (
                      <span className="text-sm text-gray-600">
                        <strong>{t.other}:</strong> {enhancedData.cloudOpsTools_other}
                      </span>
                    )}
                  </span>
                </li>
              )}

              {enhancedData.dataAnalyticsTools && (enhancedData.dataAnalyticsTools as string[]).length > 0 && (
                <li className="review-summary-item review-summary-item--stacked">
                  <span className="review-summary-label">{t.q30}</span>
                  <span className="review-summary-value review-summary-tag-list">
                    {(enhancedData.dataAnalyticsTools as string[]).map((tool, index) => (
                      <span key={index} className="inline-block px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">{tool}</span>
                    ))}
                    {enhancedData.dataAnalyticsTools_other && (
                      <span className="text-sm text-gray-600">
                        <strong>{t.other}:</strong> {enhancedData.dataAnalyticsTools_other}
                      </span>
                    )}
                  </span>
                </li>
              )}

              {enhancedData.aiMlTools && (enhancedData.aiMlTools as string[]).length > 0 && (
                <li className="review-summary-item review-summary-item--stacked">
                  <span className="review-summary-label">{t.q31}</span>
                  <span className="review-summary-value review-summary-tag-list">
                    {(enhancedData.aiMlTools as string[]).map((tool, index) => (
                      <span key={index} className="inline-block px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded">{tool}</span>
                    ))}
                    {enhancedData.aiMlTools_other && (
                      <span className="text-sm text-gray-600">
                        <strong>{t.other}:</strong> {enhancedData.aiMlTools_other}
                      </span>
                    )}
                  </span>
                </li>
              )}

              {enhancedData.cybersecurityTools && (enhancedData.cybersecurityTools as string[]).length > 0 && (
                <li className="review-summary-item review-summary-item--stacked">
                  <span className="review-summary-label">{t.q32}</span>
                  <span className="review-summary-value review-summary-tag-list">
                    {(enhancedData.cybersecurityTools as string[]).map((tool, index) => (
                      <span key={index} className="inline-block px-2 py-1 bg-red-100 text-red-800 text-xs rounded">{tool}</span>
                    ))}
                    {enhancedData.cybersecurityTools_other && (
                      <span className="text-sm text-gray-600">
                        <strong>{t.other}:</strong> {enhancedData.cybersecurityTools_other}
                      </span>
                    )}
                  </span>
                </li>
              )}

              {enhancedData.devOpsTools && (enhancedData.devOpsTools as string[]).length > 0 && (
                <li className="review-summary-item review-summary-item--stacked">
                  <span className="review-summary-label">{t.q33}</span>
                  <span className="review-summary-value review-summary-tag-list">
                    {(enhancedData.devOpsTools as string[]).map((tool, index) => (
                      <span key={index} className="inline-block px-2 py-1 bg-teal-100 text-teal-800 text-xs rounded">{tool}</span>
                    ))}
                    {enhancedData.devOpsTools_other && (
                      <span className="text-sm text-gray-600">
                        <strong>{t.other}:</strong> {enhancedData.devOpsTools_other}
                      </span>
                    )}
                  </span>
                </li>
              )}

              {enhancedData.versionControlTools && (enhancedData.versionControlTools as string[]).length > 0 && (
                <li className="review-summary-item review-summary-item--stacked">
                  <span className="review-summary-label">{t.q34}</span>
                  <span className="review-summary-value review-summary-tag-list">
                    {(enhancedData.versionControlTools as string[]).map((tool, index) => (
                      <span key={index} className="inline-block px-2 py-1 bg-pink-100 text-pink-800 text-xs rounded">{tool}</span>
                    ))}
                    {enhancedData.versionControlTools_other && (
                      <span className="text-sm text-gray-600">
                        <strong>{t.other}:</strong> {enhancedData.versionControlTools_other}
                      </span>
                    )}
                  </span>
                </li>
              )}

              {enhancedData.enterpriseTools && (enhancedData.enterpriseTools as string[]).length > 0 && (
                <li className="review-summary-item review-summary-item--stacked">
                  <span className="review-summary-label">{t.q35}</span>
                  <span className="review-summary-value review-summary-tag-list">
                    {(enhancedData.enterpriseTools as string[]).map((tool, index) => (
                      <span key={index} className="inline-block px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">{tool}</span>
                    ))}
                    {enhancedData.enterpriseTools_other && (
                      <span className="text-sm text-gray-600">
                        <strong>{t.other}:</strong> {enhancedData.enterpriseTools_other}
                      </span>
                    )}
                  </span>
                </li>
              )}

              {enhancedData.projectCollabTools && (enhancedData.projectCollabTools as string[]).length > 0 && (
                <li className="review-summary-item review-summary-item--stacked">
                  <span className="review-summary-label">{t.q36}</span>
                  <span className="review-summary-value review-summary-tag-list">
                    {(enhancedData.projectCollabTools as string[]).map((tool, index) => (
                      <span key={index} className="inline-block px-2 py-1 bg-cyan-100 text-cyan-800 text-xs rounded">{tool}</span>
                    ))}
                    {enhancedData.projectCollabTools_other && (
                      <span className="text-sm text-gray-600">
                        <strong>{t.other}:</strong> {enhancedData.projectCollabTools_other}
                      </span>
                    )}
                  </span>
                </li>
              )}

              {enhancedData.projectMgmtMethodologies && (enhancedData.projectMgmtMethodologies as string[]).length > 0 && (
                <li className="review-summary-item review-summary-item--stacked">
                  <span className="review-summary-label">{t.q37}</span>
                  <span className="review-summary-value review-summary-tag-list">
                    {(enhancedData.projectMgmtMethodologies as string[]).map((methodology, index) => (
                      <span key={index} className="inline-block px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">{methodology}</span>
                    ))}
                    {enhancedData.projectMgmtMethodologies_other && (
                      <span className="text-sm text-gray-600">
                        <strong>{t.other}:</strong> {enhancedData.projectMgmtMethodologies_other}
                      </span>
                    )}
                  </span>
                </li>
              )}
            </ol>
          </div>

          {/* Additional Information - Questions 38 */}
          {(enhancedData.additionalSkills ||
            enhancedData.greatestAchievement ||
            enhancedData.additionalInformation) && (
            <div className="p-6">
              <h3
                className="text-lg font-semibold text-gray-900 mb-4 cursor-pointer hover:text-primary-600 transition-colors flex items-center"
                onClick={() => navigateToStep?.(8)}
                title="Click to edit this section"
              >
                {t.additionalInfoHeader}
                <svg
                  className="w-4 h-4 ml-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </h3>
              <div className="space-y-4 text-sm">
                {enhancedData.additionalInformation && (
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">
                      38. {t.q40}
                    </h4>
                    <p className="text-gray-600 bg-gray-50 p-3 rounded-md">
                      {enhancedData.additionalInformation}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Validation and Submission Errors */}
        {(validationErrors.length > 0 || submissionErrors.length > 0) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  {validationErrors.length > 0
                    ? "Missing Required Information"
                    : t.submissionError}
                </h3>
                <div
                  className="mt-2 text-sm text-red-700"
                  style={{ whiteSpace: "pre-line" }}
                >
                  <ul className="list-disc list-inside space-y-1">
                    {validationErrors.map((error, index) => (
                      <li key={index}>
                        {"Please provide:"} {error}
                      </li>
                    ))}
                    {submissionErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Navigation */}
        {isMobile ? (
          <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onPrevious}
              className="w-full sm:w-auto px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              {t.previousButton || "Previous"}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`w-full sm:w-auto px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center ${
                isSubmitting
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed border-2 border-gray-500"
                  : "bg-[#D7E5F5] text-[#2B4380] hover:bg-[#D0DFF0] border-2 border-[#2B4380]"
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {"Submitting Application..."}
                </>
              ) : (
                t.submitApplicationButton || "Submit Application"
              )}
            </button>
          </div>
        ) : (
          <div className="flex justify-between pt-6">
            <button
              type="button"
              onClick={onPrevious}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              {t.previousButton || "Previous"}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center ${
                isSubmitting
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed border-2 border-gray-500"
                  : "bg-[#D7E5F5] text-[#2B4380] hover:bg-[#D0DFF0] border-2 border-[#2B4380]"
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {"Submitting Application..."}
                </>
              ) : (
                t.submitApplicationButton || "Submit Application"
              )}
            </button>
          </div>
        )}
      </motion.div>
    );
};

export default ReviewStep;
